import { Component, ViewEncapsulation, Input } from '@angular/core';
import { GlobalsService } from '../../../shared/services/globals.service';
import { InvoiceService } from '../../../shared/services/invoice.service';
import { BaseMessageInfo } from '../../../shared/models/BaseMessageInfo';
import { CommonValues } from '../../../shared/static/CommonValues';
import { FeatureFlagService } from '../../../shared/services/feature-flag.service';
import { UseFeatureFlagSet, IFeatureFlagSet } from '../../../shared/decorators/featureflags/featureflagset-decorator';

interface HistoryRow {
    // Identity
    invoiceLineId: string;
    invoiceLineHistoryId: string;
    // Display fields - typed correctly
    inventoryNumber: string;
    invoiceLineDesc: string;
    fullDescription: string;
    changeCodeDesc: string;
    resource: string;
    warehouseName: string;
    quantity: number | null;
    cost: number | null;
    totalCost: number | null;
    tax: number | null;
    markupCost: number | null;
    markup: number | null;
    sortOrder: number | null;
    price: number | null;
    amount: number | null;
    isPrintable: boolean;
    isTaxable: boolean;
    updatedBy: string;
    insertDt: Date;
    avalaraTaxTypeId: string;
    // Metadata
    isBaseline: boolean;
    changedFields: Set<string>;
}

@Component({
    selector: 'invoice-all-history',
    templateUrl: './invoice-all-history.component.html',
    styleUrls: ['./invoice-all-history.component.scss'],
    encapsulation: ViewEncapsulation.None
})
@UseFeatureFlagSet()
export class InvoiceAllHistoryComponent {

    constructor(private globals: GlobalsService, private featureFlagService: FeatureFlagService, private svc_data: InvoiceService) {}

    compFeatureFlagSet: IFeatureFlagSet = this as any;
    _active = false;
    dataLoading = false;
    historyRows: HistoryRow[] = [];
    private lineTaxTypeByLineId = new Map<string, string>();
    private parentIsAvalara: boolean = false;
    private parentTaxTypeCode: number = 0;

    @Input() showInvoiceLineCost: boolean = true;

    public show(invoiceId: string, lineTaxTypes: { invoiceLineId: string, avalaraTaxTypeId: string }[] = [], isAvalara: boolean = false, taxTypeCode: number = 0) {
        this.historyRows = [];
        this.lineTaxTypeByLineId = new Map<string, string>();
        for (const item of lineTaxTypes || []) {
            if (!item || !item.invoiceLineId) {
                continue;
            }
            const key = String(item.invoiceLineId).toLowerCase();
            const taxType = this.normalizeTaxTypeId(item.avalaraTaxTypeId);
            if (taxType) {
                this.lineTaxTypeByLineId.set(key, taxType);
            }
        }
        this.parentIsAvalara = !!isAvalara;
        this.parentTaxTypeCode = taxTypeCode || 0;
        this.dataLoading = true;
        this._active = true;

        this.svc_data.getAllLineItemHistory(invoiceId).subscribe((retData: BaseMessageInfo) => {
            if (retData.saveError) {
                this.dataLoading = false;
                return;
            }

            const allHistory = retData.primaryReturn as any[];
            if (!allHistory || allHistory.length === 0) {
                this.dataLoading = false;
                return;
            }

            this.historyRows = this.buildHistoryRows(allHistory);
            this.dataLoading = false;
        });
    }

    public close() {
        this._active = false;
    }

    // Show both columns when any history row has that Avalara tax type.
    public historyHasUseTax(): boolean {
        if (this.compFeatureFlagSet.featureFlagSet['ui-adding-tax-type-to-invoice-lines']) {
            return this.historyRows.some(e => this.isUseTaxRow(e));
        }
        return this.parentTaxTypeCode === 2;
    }

    public historyHasSalesTax(): boolean {
        if (this.compFeatureFlagSet.featureFlagSet['ui-adding-tax-type-to-invoice-lines']) {
            return this.historyRows.some(e => this.isSalesTaxRow(e));
        }
        return this.parentTaxTypeCode === 1 && this.parentIsAvalara;
    }

    public historyHasGenericTax(): boolean {
        if (this.compFeatureFlagSet.featureFlagSet['ui-adding-tax-type-to-invoice-lines']) {
            return !this.parentIsAvalara;
        }
        return false;
    }

    public isUseTaxRow(row: HistoryRow): boolean {
        return this.normalizeTaxTypeId(row?.avalaraTaxTypeId) === CommonValues.AvalaraTaxType_UseTax;
    }

    public isSalesTaxRow(row: HistoryRow): boolean {
        return this.normalizeTaxTypeId(row?.avalaraTaxTypeId) === CommonValues.AvalaraTaxType_SalesTax;
    }

    /** Tax amount only in the column matching this history row's AvalaraTaxTypeId. */
    public taxForUseColumn(row: HistoryRow): number | null {
        if (!this.compFeatureFlagSet.featureFlagSet['ui-adding-tax-type-to-invoice-lines']) {
            return row?.tax != null ? Number(row.tax) : null;
        }
        return this.isUseTaxRow(row) && row?.tax != null ? Number(row.tax) : null;
    }

    public taxForSalesColumn(row: HistoryRow): number | null {
        if (!this.compFeatureFlagSet.featureFlagSet['ui-adding-tax-type-to-invoice-lines']) {
            return row?.tax != null ? Number(row.tax) : null;
        }
        return this.isSalesTaxRow(row) && row?.tax != null ? Number(row.tax) : null;
    }

    public isCellChanged(row: HistoryRow, field: string): boolean {
        return !row.isBaseline && row.changedFields && row.changedFields.has(field);
    }

    public rowCallback = (context: { dataItem: HistoryRow }) => {
        return { 'change-row': !context.dataItem.isBaseline };
    }

    private buildHistoryRows(allHistory: any[]): HistoryRow[] {
        // Group by InvoiceLineId
        const groups = new Map<string, any[]>();
        for (const entry of allHistory) {
            const key = entry.invoiceLineId;
            if (!groups.has(key)) {
                groups.set(key, []);
            }
            groups.get(key).push(entry);
        }

        const result: HistoryRow[] = [];

        groups.forEach((entries, lineId) => {
            // Sort by InsertDt ascending
            entries.sort((a: any, b: any) => new Date(a.insertDt).getTime() - new Date(b.insertDt).getTime());

            // First entry is the baseline (original)
            result.push(this.toHistoryRow(entries[0], true, new Set<string>()));

            // Compare sequential pairs - only add rows where something changed
            for (let i = 1; i < entries.length; i++) {
                const prev = entries[i - 1];
                const curr = entries[i];
                const changedFields = this.detectChanges(prev, curr);

                if (changedFields.size > 0) {
                    result.push(this.toHistoryRow(curr, false, changedFields));
                }
            }
        });

        //return result.reverse();
        return result.sort((a, b) => b.insertDt.getTime() - a.insertDt.getTime()); // Sort by InsertDt descending
    }

    private toHistoryRow(entry: any, isBaseline: boolean, changedFields: Set<string>): HistoryRow {
        const fallback = this.lineTaxTypeByLineId.get(String(entry.invoiceLineId || '').toLowerCase()) || null;
        const rowTaxType = this.normalizeTaxTypeId(entry.avalaraTaxTypeId) || fallback;
        return {
            invoiceLineId: entry.invoiceLineId,
            invoiceLineHistoryId: entry.invoiceLineHistoryId,
            inventoryNumber: entry.inventoryNumber || '',
            invoiceLineDesc: entry.invoiceLineDesc || '',
            fullDescription: entry.fullDescription || '',
            changeCodeDesc: entry.changeCodeDesc || '',
            resource: entry.resource || '',
            warehouseName: entry.warehouseName || '',
            quantity: entry.quantity != null ? Number(entry.quantity) : null,
            cost: entry.cost != null ? Number(entry.cost) : null,
            totalCost: entry.totalCost != null ? Number(entry.totalCost) : null,
            tax: entry.tax != null ? Number(entry.tax) : null,
            markupCost: entry.markupCost != null ? Number(entry.markupCost) : null,
            markup: entry.markup != null ? Number(entry.markup) : null,
            sortOrder: entry.sortOrder != null ? Number(entry.sortOrder) : null,
            price: entry.price != null ? Number(entry.price) : null,
            amount: entry.amount != null ? Number(entry.amount) : null,
            isPrintable: !!entry.isPrintable,
            isTaxable: !!entry.isTaxable,
            updatedBy: entry.updatedBy || '',
            insertDt: new Date(entry.insertDt),
            // Prefer per-history-row AvalaraTaxTypeId from API; fall back to current line only if missing.
            avalaraTaxTypeId: rowTaxType,
            isBaseline: isBaseline,
            changedFields: changedFields
        };
    }

    private detectChanges(prev: any, curr: any): Set<string> {
        const changedFields = new Set<string>();
        const fieldsToCompare = [
            'inventoryNumber', 'invoiceLineDesc', 'fullDescription', 'changeCodeDesc',
            'resource', 'warehouseName', 'quantity', 'cost', 'totalCost', 'tax',
            'markupCost', 'markup', 'sortOrder', 'price', 'amount', 'isPrintable', 'isTaxable',
            'avalaraTaxTypeId'
        ];

        for (const field of fieldsToCompare) {
            const prevVal = field === 'avalaraTaxTypeId'
                ? this.normalizeTaxTypeId(prev[field])
                : this.normalizeValue(prev[field]);
            const currVal = field === 'avalaraTaxTypeId'
                ? this.normalizeTaxTypeId(curr[field])
                : this.normalizeValue(curr[field]);
            if (prevVal !== currVal) {
                changedFields.add(field);
            }
        }

        return changedFields;
    }

    private normalizeValue(val: any): string {
        if (val === null || val === undefined) return '__null__';
        if (typeof val === 'boolean') return val ? 'true' : 'false';
        if (typeof val === 'number') return val.toString();
        return val.toString().trim();
    }

    private normalizeTaxTypeId(val: any): string {
        if (val === null || val === undefined || val === '') {
            return null;
        }
        return String(val).toLowerCase();
    }
}
